"use client";

import { useCallback, useMemo, useState } from "react";
import {
  type BriefGroup,
  groupBriefsByCategory,
} from "../../_lib/brief-grouping";
import type { ExploreBriefCard } from "../../types";
import { SectionHeader } from "../section-header";
import { BriefPreviewDialog } from "./brief-preview-dialog";
import { BriefStoriesVerticalOverlay } from "./briefs-stories-vertical-overlay";
import { PlayAllButton } from "./play-all-button";
import { useBriefsOverlay } from "./use-briefs-overlay";

const MAX_VISIBLE = 3;

// ─── Category dot color ──────────────────────────────────────

const CATEGORY_DOT_COLORS: Record<string, string> = {
  "agentic-engineering": "var(--explore-accent-teal)",
  "models-and-apis": "var(--explore-accent-orange)",
  "inference-and-infrastructure": "var(--explore-accent-yellow)",
  "security-and-reliability": "var(--explore-accent-red)",
  "cost-and-operations": "var(--explore-accent-deal)",
  "research-and-benchmarks": "var(--explore-accent-purple)",
  "business-and-policy": "var(--explore-text-muted)",
  "video-and-film": "var(--explore-accent-red)",
  "image-and-design": "var(--explore-accent-purple)",
  "audio-and-voice": "var(--explore-accent-orange)",
  "product-design": "var(--explore-accent-teal)",
  "vibe-coding": "var(--explore-accent-deal)",
  writing: "var(--explore-text-muted)",
  other: "var(--explore-text-muted)",
};

function getCategoryDotColor({ slug }: { slug: string }): string {
  return CATEGORY_DOT_COLORS[slug] ?? "var(--explore-text-muted)";
}

// ─── Compact brief row ───────────────────────────────────────

function CompactBriefRow({
  brief,
  categorySlug,
  onClick,
}: {
  brief: ExploreBriefCard;
  categorySlug: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: getCategoryDotColor({ slug: categorySlug }) }}
      />
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        {brief.title}
      </p>
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Category group — NO chevrons, NO collapse ───────────────

function CategoryGroup({
  group,
  onBriefClick,
}: {
  group: BriefGroup;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = group.briefs.length > MAX_VISIBLE;
  const remainingCount = group.briefs.length - MAX_VISIBLE;
  const visibleBriefs = isExpanded
    ? group.briefs
    : group.briefs.slice(0, MAX_VISIBLE);

  return (
    <div>
      {/* Category header — no chevron, always visible */}
      <div
        className="mb-1.5 flex items-center gap-2 px-4 lg:px-0"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <span className="text-[0.78rem]">{group.categoryEmoji}</span>
        <span className="font-semibold text-[0.78rem] text-[var(--explore-text-primary)]">
          {group.categoryDisplayName}
        </span>
        <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
          ({group.briefs.length})
        </span>
      </div>

      {/* Brief items in card */}
      <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
        <div className="divide-y divide-[var(--explore-border-light)]">
          {visibleBriefs.map((brief) => (
            <CompactBriefRow
              brief={brief}
              categorySlug={group.categorySlug}
              key={brief.briefId}
              onClick={() => onBriefClick(brief)}
            />
          ))}
        </div>

        {/* Show more — subtle inline text, no divider */}
        {hasMore && !isExpanded && (
          <button
            className="w-full py-2 text-center text-[0.72rem] text-[var(--explore-text-muted)] transition-colors hover:text-[var(--explore-text-primary)]"
            onClick={() => setIsExpanded(true)}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
            type="button"
          >
            + {remainingCount} more briefs
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface BriefsCompactSectionProps {
  briefs: ExploreBriefCard[];
  briefsReportDate: string;
  exploreBase: string;
  className?: string;
}

export function BriefsCompactSection({
  briefs,
  briefsReportDate,
  className,
}: BriefsCompactSectionProps) {
  const {
    selectedBrief,
    dialogOpen,
    setDialogOpen,
    storiesOpen,
    handleBriefClick,
    handlePlayAll,
    handleCloseStories,
    allViewed,
    handleAllViewed,
  } = useBriefsOverlay();

  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const group = groups.find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [groups, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  if (briefs.length === 0) {
    return null;
  }

  const dateLabel = formatBriefsDate({ reportDate: briefsReportDate });

  return (
    <section className={className}>
      <SectionHeader subtitle={dateLabel} title="Briefs">
        <PlayAllButton
          allViewed={allViewed}
          onPlay={handlePlayAll}
          totalBriefs={briefs.length}
        />
      </SectionHeader>

      <div className="flex flex-col gap-4 px-1 lg:px-0">
        {groups.map((group) => (
          <CategoryGroup
            group={group}
            key={group.categorySlug}
            onBriefClick={handleBriefClick}
          />
        ))}
      </div>

      <BriefPreviewDialog
        brief={selectedBrief}
        categoryBriefs={categoryBriefs}
        onNavigateBrief={handleNavigateBrief}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
      />

      {storiesOpen && (
        <BriefStoriesVerticalOverlay
          briefs={briefs}
          onAllViewed={handleAllViewed}
          onClose={handleCloseStories}
        />
      )}
    </section>
  );
}

function formatBriefsDate({ reportDate }: { reportDate: string }): string {
  const [year, month, day] = reportDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}
